import { useSyncExternalStore } from "react";

// In-memory-only auth state, written exclusively by the postMessage handshake
// (src/services/handshake.ts). The credential must never touch a URL, DOM
// attribute, or any storage API — module state is the whole persistence story.
// Sole exception: the bank sign-in popup URL (ssoSignInUrl in
// src/renderers/bank/sso.ts), where cortex takes the auth token as a query
// param because a popup navigation can't carry headers.

export interface EmbedAuth {
  /**
   * The legacy API-key credential received over postMessage, sent to cortex
   * as `X-API-Key`. Being phased out in favor of `authToken`; the handshake
   * guarantees at least one of the two is present.
   */
  apiKey?: string;
  /**
   * The auth-token credential received over postMessage, sent to cortex as
   * `Authorization: Bearer`. Preferred over `apiKey` when both arrive; will
   * eventually be the only credential.
   */
  authToken?: string;
  /**
   * The validated origin of the embedding page — taken from the `auth`
   * MessageEvent's `origin` (browser-asserted), never from
   * `document.referrer`. Sent to cortex as `X-Aiera-Parent-Origin`.
   */
  parentOrigin: string;
  /**
   * The embedding host component's self-reported identity (e.g.
   * "@aiera-inc/components-content" from the web component), delivered
   * alongside the credential in `auth` and normalized by the handshake. Sent
   * to cortex as `X-Aiera-Host-Id`; absent when the host doesn't send one.
   * Advisory only — never part of any security decision.
   */
  hostId?: string;
  /**
   * The embedding host component's self-reported version (e.g. "1.0.0"),
   * delivered and normalized like `hostId`. Sent to cortex as
   * `X-Aiera-Host-Version` for analytics and version-specific debugging;
   * absent when the host doesn't send one. Advisory only — never part of any
   * security decision.
   */
  hostVersion?: string;
}

let auth: EmbedAuth | null = null;
const listeners = new Set<() => void>();

export function setEmbedAuth(next: EmbedAuth): void {
  // Hosts may deliver the same `auth` more than once (e.g. answering every
  // retried `ready`). Dropping no-op updates lets subscribers treat any
  // store change as a real credential change.
  if (
    auth !== null &&
    auth.apiKey === next.apiKey &&
    auth.authToken === next.authToken &&
    auth.parentOrigin === next.parentOrigin &&
    auth.hostId === next.hostId &&
    auth.hostVersion === next.hostVersion
  ) {
    return;
  }
  auth = next;
  for (const listener of listeners) listener();
}

export function getEmbedAuth(): EmbedAuth | null {
  return auth;
}

export function subscribeEmbedAuth(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useEmbedAuth(): EmbedAuth | null {
  return useSyncExternalStore(subscribeEmbedAuth, getEmbedAuth);
}

/** Test-only reset. */
export function clearEmbedAuth(): void {
  auth = null;
  for (const listener of listeners) listener();
}
